import React, { useState, useEffect } from 'react';
import { FaPlus, FaEye, FaEdit, FaUsers, FaSave, FaTrash, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from "./Sidebar";

const API_URL = '/api';

const DashboardBuilderMain = () => {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDashboardCode, setNewDashboardCode] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const navigate = useNavigate();

  // Theme variables
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444",
      "--inactive-text": "#6b7280",
      "--inactive-surface": "#fafaf9",
      "--active-pill-bg": "#e6f6f3",
      "--active-pill-text": "#0f766e",
      "--inactive-pill-bg": "#fafaf9",
      "--inactive-pill-text": "#6b7280"
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, []);

  // Fetch dashboards
  useEffect(() => {
    fetchDashboards();
    fetchUsers();
  }, []);

  const fetchDashboards = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const response = await fetch(`${API_URL}/dashboards`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch dashboards');
      
      const data = await response.json();
      setDashboards(data);
    } catch (error) {
      console.error('Error fetching dashboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const response = await fetch(`${API_URL}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateDashboard = () => {
    setShowCreateModal(true);
  };

  const handleSaveNewDashboard = async () => {
    if (!newDashboardName.trim() || !newDashboardCode.trim()) {
      alert('Please enter both dashboard name and code');
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken') || '';
      const response = await fetch(`${API_URL}/dashboards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          DashboardName: newDashboardName,
          DashboardCode: newDashboardCode,
          DesignJSON: JSON.stringify({ elements: [] })
        })
      });

      if (!response.ok) throw new Error('Failed to create dashboard');
      
      const newDashboard = await response.json();
      setDashboards([...dashboards, newDashboard]);
      setShowCreateModal(false);
      setNewDashboardName('');
      setNewDashboardCode('');
      setShowSaveConfirmation(true);
      
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => setShowSaveConfirmation(false), 3000);
    } catch (error) {
      console.error('Error creating dashboard:', error);
      alert('Failed to create dashboard. Please try again.');
    }
  };

  const handleViewDashboard = (dashboardCode) => {
    window.open(`/dashboard-view/${dashboardCode}`, '_blank');
  };

  const handleEditDashboard = (dashboardCode) => {
    window.open(`/dashboard-builder/${dashboardCode}`, '_blank');
  };

  const handleAssignUsers = (dashboard) => {
    setSelectedDashboard(dashboard);
    
    // Get already assigned users
    const assignedUserIds = dashboard.assignedUsers?.map(user => user.UserID) || [];
    setSelectedUsers(assignedUserIds);
    
    setShowAssignModal(true);
  };

  const handleSaveAssignments = async () => {
    if (!selectedDashboard) return;
    
    try {
      const token = localStorage.getItem('authToken') || '';
      
      // First, remove existing assignments
      await fetch(`${API_URL}/user-dashboards/dashboard/${selectedDashboard.DashboardID}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Then add new assignments
      if (selectedUsers.length > 0) {
        const assignments = selectedUsers.map(userId => ({
          UserID: userId,
          DashboardID: selectedDashboard.DashboardID
        }));
        
        await fetch(`${API_URL}/user-dashboards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(assignments)
        });
      }
      
      // Update local state
      const updatedDashboards = dashboards.map(d => {
        if (d.DashboardID === selectedDashboard.DashboardID) {
          const assignedUsers = users.filter(user => selectedUsers.includes(user.UserID));
          return { ...d, assignedUsers };
        }
        return d;
      });
      
      setDashboards(updatedDashboards);
      setShowAssignModal(false);
      setShowSaveConfirmation(true);
      
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => setShowSaveConfirmation(false), 3000);
    } catch (error) {
      console.error('Error saving assignments:', error);
    }
  };

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleDeleteConfirmation = (dashboard) => {
    setDashboardToDelete(dashboard);
    setShowDeleteModal(true);
  };

  const handleDeleteDashboard = async () => {
    if (!dashboardToDelete) return;
    
    try {
      const token = localStorage.getItem('authToken') || '';
      const response = await fetch(`${API_URL}/dashboards/${dashboardToDelete.DashboardID}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete dashboard');
      
      // Remove dashboard from state
      setDashboards(dashboards.filter(d => d.DashboardID !== dashboardToDelete.DashboardID));
      setShowDeleteModal(false);
      setDashboardToDelete(null);
      setShowSaveConfirmation(true);
      
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => setShowSaveConfirmation(false), 3000);
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      alert('Failed to delete dashboard. Please try again.');
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  // Theme object for Sidebar
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 mx-auto" style={{ borderColor: 'var(--accent-color)' }}></div>
          <p className="mt-4" style={{ color: 'var(--text-muted)' }}>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)', color: 'var(--text-primary)' }}>
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={setSidebarCollapsed} 
        theme={sidebarTheme}
        onLogout={handleLogout}
      />
      
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard Management</h1>
            <p className="text-gray-600 mt-1">Create, manage and assign dashboards</p>
          </div>
          <button
            onClick={handleCreateDashboard}
            className="flex items-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <FaPlus className="mr-2" /> Design Dashboard
          </button>
        </div>

        {/* Save Confirmation */}
        {showSaveConfirmation && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center animate-fadeIn">
            <FaSave className="mr-2" />
            <span>Operation completed successfully!</span>
          </div>
        )}

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboards.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white rounded-xl shadow-sm">
              <div className="text-gray-500 mb-4">No dashboards found</div>
              <button
                onClick={handleCreateDashboard}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg hover:shadow-md transition-all duration-300"
              >
                Create Your First Dashboard
              </button>
            </div>
          ) : (
            dashboards.map(dashboard => (
              <div 
                key={dashboard.DashboardID} 
                className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-lg hover:border-blue-200"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-gray-800 truncate">{dashboard.DashboardName}</h3>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ml-2">
                      {dashboard.DashboardCode}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600 mt-3">
                    <FaCalendarAlt className="mr-2 text-blue-500" />
                    <span>Created: {new Date(dashboard.CreatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-5">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg">
                      <FaUsers className="mr-2 text-blue-500" />
                      <span>{dashboard.assignedUsers?.length || 0} users assigned</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between pt-3 border-t border-gray-100">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDashboard(dashboard.DashboardCode)}
                        className="p-2.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-300 text-blue-600 hover:text-blue-800 group"
                        title="View Dashboard"
                      >
                        <FaEye className="group-hover:scale-110 transition-transform" />
                      </button>
                      <button
                        onClick={() => handleEditDashboard(dashboard.DashboardCode)}
                        className="p-2.5 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all duration-300 text-indigo-600 hover:text-indigo-800 group"
                        title="Edit Dashboard"
                      >
                        <FaEdit className="group-hover:scale-110 transition-transform" />
                      </button>
                      <button
                        onClick={() => handleAssignUsers(dashboard)}
                        className="p-2.5 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all duration-300 text-purple-600 hover:text-purple-800 group"
                        title="Assign Users"
                      >
                        <FaUsers className="group-hover:scale-110 transition-transform" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => handleDeleteConfirmation(dashboard)}
                      className="p-2.5 bg-red-50 rounded-lg hover:bg-red-100 transition-all duration-300 text-red-600 hover:text-red-800 group"
                      title="Delete Dashboard"
                    >
                      <FaTrash className="group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Dashboard Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Create New Dashboard</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard Name</label>
                <input
                  type="text"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter dashboard name"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard Code</label>
                <input
                  type="text"
                  value={newDashboardCode}
                  onChange={(e) => setNewDashboardCode(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter dashboard code"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNewDashboard}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg hover:shadow-md transition-all duration-300"
                >
                  Create Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Users Modal */}
        {showAssignModal && selectedDashboard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Assign Users to Dashboard</h2>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-gray-800">{selectedDashboard.DashboardName}</h3>
                <p className="text-sm text-gray-600">ID: {selectedDashboard.DashboardCode}</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Users</label>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {users.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">No users available</div>
                  ) : (
                    users.map(user => (
                      <label key={user.UserID} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.UserID)}
                          onChange={() => toggleUserSelection(user.UserID)}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="font-medium text-gray-800">{user.FullName}</div>
                          <div className="text-xs text-gray-500">{user.Email} • {user.Role}</div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignments}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg hover:shadow-md transition-all duration-300"
                >
                  Save Assignments
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && dashboardToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Confirm Deletion</h2>
              <div className="mb-6">
                <p className="text-gray-700">Are you sure you want to delete the dashboard <span className="font-semibold">{dashboardToDelete.DashboardName}</span>?</p>
                <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDashboardToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDashboard}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-md transition-all duration-300"
                >
                  Delete Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardBuilderMain;